import React, { useState, useEffect, useCallback } from 'react';
import { 
  EyeIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon,
  PencilIcon,
  PhotoIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import knowledgeService from '../../services/knowledgeService';

interface TextExtraction {
  id: number;
  original_filename: string;
  file_path: string;
  file_type: string;
  extraction_method: string;
  extracted_text: string;
  reviewed_text: string | null;
  confidence_score: number;
  status: 'pending' | 'reviewed' | 'approved' | 'rejected';
  extraction_metadata: any;
  extracted_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

interface ExtractionStats {
  total: number;
  byStatus: {
    pending?: number;
    approved?: number;
    rejected?: number;
    reviewed?: number;
  };
  averageConfidence: number;
  recentExtractions: number;
}

const TextExtractionReviewer: React.FC = () => {
  const [extractions, setExtractions] = useState<TextExtraction[]>([]);
  const [stats, setStats] = useState<ExtractionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedExtraction, setSelectedExtraction] = useState<TextExtraction | null>(null);
  const [editedText, setEditedText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  const fetchExtractions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await knowledgeService.getTextExtractions(statusFilter);
      setExtractions(response.extractions);
    } catch (error) {
      console.error('Failed to fetch text extractions:', error);
      toast.error('Failed to load text extractions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await knowledgeService.getExtractionStats();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchExtractions();
    fetchStats();
  }, [fetchExtractions, fetchStats]);

  const handleSelectExtraction = (extraction: TextExtraction) => {
    setSelectedExtraction(extraction);
    setEditedText(extraction.reviewed_text || extraction.extracted_text);
    setIsEditing(false);
  };

  const handleUpdateExtraction = async (status: string) => {
    if (!selectedExtraction) return;

    try {
      setProcessingAction(status);
      await knowledgeService.updateTextExtraction(selectedExtraction.id, {
        reviewed_text: editedText,
        status
      });
      
      toast.success(`Text extraction ${status} successfully`);
      
      // Refresh data
      await fetchExtractions();
      await fetchStats();
      
      // Update selected extraction
      setSelectedExtraction({
        ...selectedExtraction,
        reviewed_text: editedText,
        status: status as any,
        reviewed_at: new Date().toISOString()
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error(`Failed to ${status} extraction:`, error);
      toast.error(`Failed to ${status} text extraction`);
    } finally {
      setProcessingAction(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-50';
      case 'rejected': return 'text-red-600 bg-red-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <PhotoIcon className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Text Extraction Review</h2>
              <p className="text-gray-600 mt-1">
                Review and approve AI-extracted text from images and documents
              </p>
            </div>
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center space-x-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-blue-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Extractions</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-yellow-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.byStatus.pending || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Approved</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.byStatus.approved || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-blue-600">%</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Avg Confidence</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {Math.round(stats.averageConfidence * 100)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Extraction List */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Text Extractions</h3>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6">
                <div className="animate-pulse space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-gray-200 rounded"></div>
                  ))}
                </div>
              </div>
            ) : extractions.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <PhotoIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No text extractions found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {extractions.map((extraction) => (
                  <div
                    key={extraction.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedExtraction?.id === extraction.id ? 'bg-purple-50 border-r-4 border-purple-500' : ''
                    }`}
                    onClick={() => handleSelectExtraction(extraction)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {extraction.original_filename}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(extraction.extracted_at)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(extraction.status)}`}>
                          {extraction.status}
                        </span>
                        <span className={`text-xs font-medium ${getConfidenceColor(extraction.confidence_score)}`}>
                          {Math.round(extraction.confidence_score * 100)}%
                        </span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {extraction.extracted_text.substring(0, 100)}...
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Review Panel */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Review Panel</h3>
              {selectedExtraction && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="inline-flex items-center px-3 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded-md"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  {isEditing ? 'Cancel' : 'Edit'}
                </button>
              )}
            </div>
          </div>
          
          {selectedExtraction ? (
            <div className="p-6 space-y-4">
              {/* File Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">File Information</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <p><span className="font-medium">Filename:</span> {selectedExtraction.original_filename}</p>
                  <p><span className="font-medium">Type:</span> {selectedExtraction.file_type}</p>
                  <p><span className="font-medium">Method:</span> {selectedExtraction.extraction_method}</p>
                  <p><span className="font-medium">Confidence:</span> 
                    <span className={`ml-1 font-medium ${getConfidenceColor(selectedExtraction.confidence_score)}`}>
                      {Math.round(selectedExtraction.confidence_score * 100)}%
                    </span>
                  </p>
                </div>
              </div>
              
              {/* Text Review */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Extracted Text</h4>
                {isEditing ? (
                  <div className="space-y-3">
                    <textarea
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 text-sm"
                      placeholder="Edit the extracted text..."
                    />
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleUpdateExtraction('approved')}
                        disabled={processingAction === 'approved'}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        {processingAction === 'approved' ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleUpdateExtraction('rejected')}
                        disabled={processingAction === 'rejected'}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        <XCircleIcon className="h-4 w-4 mr-2" />
                        {processingAction === 'rejected' ? 'Rejecting...' : 'Reject'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-700">
                      {selectedExtraction.reviewed_text || selectedExtraction.extracted_text}
                    </pre>
                  </div>
                )}
              </div>
              
              {/* Status and Actions */}
              {selectedExtraction.status === 'pending' && !isEditing && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    Review
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <EyeIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Select an extraction to review</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextExtractionReviewer;